import { formatEther } from '@ethersproject/units'
import { Trans } from '@lingui/macro'
import { formatNumber, NumberType } from '@x-swap-protocol/conedison/format'
import { useWeb3React } from '@web3-react/core'
import { WXDC_XDC } from '@xspswap/smart-order-router'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import {
  LARGE_MEDIA_BREAKPOINT,
  MAX_WIDTH_MEDIA_BREAKPOINT,
  MEDIUM_MEDIA_BREAKPOINT,
  SMALL_MEDIA_BREAKPOINT,
} from 'components/Tokens/constants'
import { MouseoverTooltip } from 'components/Tooltip'
import { format } from 'date-fns'
import { formatUnits } from 'ethers/lib/utils'
import { useCurrency } from 'hooks/Tokens'
import { getUniswapV3Pool, useUniswapV3Staker } from 'hooks/useContract'
import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react'
import { Info } from 'react-feather'
import { Link } from 'react-router-dom'
import styled, { css, useTheme } from 'styled-components/macro'
import { ClickableStyle } from 'theme'

import { EthPrices } from '../../../hooks/useEthPrices'
import { getFormattedTime } from '../../../utils/date'
import { IFarm } from '../interfaces'

enum TokenSortMethod {
  POOL = 'POOL',
  DURATION = 'Duration UTC time',
  VESTING_PERIOD = 'Vesting period',
  STAKED = 'Staked',
  TOTAL_REWARD = 'Total reward',
  REWARD_TOKEN = 'Reward token',
  REWARD_APR = 'Reward apr',
}

const HEADER_DESCRIPTIONS: Record<TokenSortMethod, ReactNode | undefined> = {
  [TokenSortMethod.POOL]: undefined,
  [TokenSortMethod.DURATION]: undefined,
  [TokenSortMethod.TOTAL_REWARD]: <Trans>Total rewards to be distributed as liquidity incentives.</Trans>,
  [TokenSortMethod.REWARD_TOKEN]: undefined,
  [TokenSortMethod.VESTING_PERIOD]: undefined,
  [TokenSortMethod.STAKED]: undefined,
  [TokenSortMethod.REWARD_APR]: <Trans>rewards_apr = (rewards_per_year / pool_tvl) * 100</Trans>,
}

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const ClockWrapper = styled.div`
  position: absolute;
  z-index: 2;
  left: -23px;
  top: 2px;
`

const DataCell = styled(Cell)<{ sortable: boolean }>`
  justify-content: flex-end;
  min-width: 80px;
  user-select: ${({ sortable }) => (sortable ? 'none' : 'unset')};
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
`

const PoolCell = styled(Cell)`
  justify-content: flex-start;
  padding: 0 8px;
  min-width: 220px;
  gap: 8px;
`

const DurationCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  min-width: 150px;

  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const VestingPeriodCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const ListNumberCell = styled(Cell)<{ header: boolean }>`
  color: ${({ theme }) => theme.textSecondary};
  min-width: 32px;
  font-size: 14px;

  /* @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  } */
`

const PoolTvlCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const TotalRewardCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const RewardAprCell = styled(DataCell)`
  /* padding-right: 8px; */
  justify-content: center;
  @media only screen and (max-width: ${'370px'}) {
    display: none;
  }
`

const StyledTokenRow = styled.div<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  background-color: transparent;
  display: grid;
  font-size: 16px;
  grid-template-columns: 0.8fr 1.5fr 2fr 1.5fr 1fr 2.5fr 1.5fr;
  line-height: 24px;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  min-width: 390px;
  ${({ first, last }) => css`
    height: ${first || last ? '72px' : '64px'};
    padding-top: ${first ? '8px' : '0px'};
    padding-bottom: ${last ? '8px' : '0px'};
  `}
  padding-left: 12px;
  padding-right: 12px;
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  width: 100%;
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }

  @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
    grid-template-columns: 0.8fr 1.5fr 2fr 1.5fr 1fr 2.5fr 1.5fr;
  }

  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    grid-template-columns: 0.8fr 1.5fr 1fr 2.5fr 1.5fr;
  }

  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    grid-template-columns: 0.8fr 1.5fr 1.5fr;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    grid-template-columns: 0.7fr 1.2fr 1fr;
    min-width: unset;
    border-bottom: 0.5px solid ${({ theme }) => theme.backgroundModule};

    :last-of-type {
      border-bottom: none;
    }
  }
`

const StyledHeaderRow = styled(StyledTokenRow)`
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px 8px 0px 0px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  height: 48px;
  line-height: 16px;
  padding: 0px 12px;
  width: 100%;
  justify-content: center;
  &:hover {
    background-color: transparent;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: space-between;
  }
`

/* Token Row: skeleton row component */
function TokenRow({
  header,
  listNumber,
  poolTvl,
  totalReward,
  duration,
  vestingPeriod,
  pool,
  rewardApr,
  // sparkLine,
  ...rest
}: {
  first?: boolean
  header: boolean
  listNumber?: ReactNode
  loading?: boolean
  pool: ReactNode
  duration: ReactNode
  vestingPeriod: ReactNode
  // sparkLine?: ReactNode
  poolTvl: ReactNode
  rewardApr: ReactNode
  totalReward?: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowCells = (
    <>
      <ListNumberCell header={header}>{listNumber}</ListNumberCell>
      <PoolCell data-testid="pool-cell">{pool}</PoolCell>
      <DurationCell data-testid="duration-cell" sortable={header}>
        {duration}
      </DurationCell>
      <VestingPeriodCell data-testid="vesting_period-cell" sortable={header}>
        {vestingPeriod}
      </VestingPeriodCell>
      <PoolTvlCell data-testid="poolTvl-cell" sortable={header}>
        {poolTvl}
      </PoolTvlCell>
      <TotalRewardCell data-testid="totalReward-cell" sortable={header}>
        {totalReward}
      </TotalRewardCell>
      <RewardAprCell data-testid="reward_apr-cell" sortable={header}>
        {rewardApr}
      </RewardAprCell>
      {/* <SparkLineCell>{sparkLine}</SparkLineCell> */}
    </>
  )
  if (header) return <StyledHeaderRow data-testid="header-row">{rowCells}</StyledHeaderRow>
  return <StyledTokenRow {...rest}>{rowCells}</StyledTokenRow>
}

const HeaderCellWrapper = styled.span<{ onClick?: () => void; textAlign?: 'left' | 'right' | 'center' }>`
  align-items: center;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'unset')};
  display: flex;
  gap: 4px;
  justify-content: ${({ textAlign }) =>
    textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center'};
  width: 100%;
  &:hover {
    ${ClickableStyle}
  }
`
const InfoIconContainer = styled.div`
  margin-left: 2px;
  display: flex;
  align-items: center;
  cursor: help;
`

const EndedContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.red300};
  color: ${({ theme }) => theme.red300};
  font-size: 14px;
`

const ClickableContent = styled.div<{ gap?: string; color?: string; borderLeft?: string; padding?: string }>`
  position: relative;
  display: flex;
  text-decoration: none;
  color: ${({ theme, color }) => color || theme.textPrimary};
  border-left: ${({ borderLeft }) => borderLeft || ''};
  padding: ${({ padding }) => padding || ''};
  align-items: center;
  cursor: pointer;
  gap: ${({ gap }) => gap || ''};
`

/* Get singular header cell for header row */
function HeaderCell({
  category,
  textAlign = 'right',
}: {
  category: TokenSortMethod // TODO: change this to make it work for trans
  textAlign?: 'left' | 'right' | 'center'
}) {
  const description = HEADER_DESCRIPTIONS[category]

  return (
    <HeaderCellWrapper
      onClick={
        // handleSortCategory
        () => console.log(888)
      }
      textAlign={textAlign}
    >
      {/* {sortMethod === category && (
        <>
          {sortAscending ? (
            <ArrowUp size={20} strokeWidth={1.8} color={theme.accentActive} />
          ) : (
            <ArrowDown size={20} strokeWidth={1.8} color={theme.accentActive} />
          )}
        </>
      )} */}
      {category}
      {description && (
        <MouseoverTooltip text={description} placement="right">
          <InfoIconContainer>
            <Info size={14} />
          </InfoIconContainer>
        </MouseoverTooltip>
      )}
    </HeaderCellWrapper>
  )
}

/* Header Row: top header row component for table */
export function HeaderRow() {
  return (
    <TokenRow
      header={true}
      listNumber="#"
      duration={<HeaderCell category={TokenSortMethod.DURATION} textAlign="center" />}
      pool={<HeaderCell category={TokenSortMethod.POOL} textAlign="left" />}
      poolTvl={<HeaderCell category={TokenSortMethod.STAKED} textAlign="center" />}
      rewardApr={<HeaderCell category={TokenSortMethod.REWARD_APR} textAlign="center" />}
      vestingPeriod={<HeaderCell category={TokenSortMethod.VESTING_PERIOD} textAlign="center" />}
      totalReward={<HeaderCell category={TokenSortMethod.TOTAL_REWARD} textAlign="center" />}
      // sparkLine={null}
    />
  )
}

const StyledLink = styled(Link)`
  text-decoration: none;
`

const FlexCol = styled.div<{ width?: string; maxWidth?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: ${({ width }) => width || ''};
  max-width: ${({ maxWidth }) => maxWidth || ''};
`

interface ILoadedRowProps {
  farm: IFarm
  farmListIndex: number
  farmListLength: number
  xdcPrice?: EthPrices
  rewardTokenPrice: string
}

/* Loaded State: row component with token information */
export const LoadedRow = ({ farmListIndex, farmListLength, farm, xdcPrice, rewardTokenPrice }: ILoadedRowProps) => {
  const theme = useTheme()
  const uniswapV3Staker = useUniswapV3Staker()
  const { provider } = useWeb3React()
  const [tokens, setTokens] = useState({
    token0: '',
    token1: '',
  })
  const token0 = useCurrency(tokens.token0)
  const token1 = useCurrency(tokens.token1)

  const {
    poolTitle,
    endTime,
    startTime,
    rewardToken,
    vestingPeriod,
    poolTvlInXdc,
    stakedTvlInXdc,
    totalReward,
    rewardSymbol,
    pool,
  } = farm

  const rewardDecimals = useMemo(() => {
    if (token0 && token1) {
      if (tokens.token0.toLowerCase() === rewardToken.toLowerCase()) {
        return token0.decimals
      }
      if (tokens.token1.toLowerCase() === rewardToken.toLowerCase()) {
        return token1.decimals
      }
    }
    return 18
  }, [rewardToken, token0, token1, tokens])
  const fetchFarmDetails = async () => {
    if (!pool || !provider || !uniswapV3Staker) return

    const poolContract = getUniswapV3Pool(pool, provider)
    if (!poolContract) return
    Promise.all([poolContract?.token0(), poolContract?.token1()]).then(([token0, token1]) => {
      setTokens({
        token0,
        token1,
      })
    })
  }
  useEffect(() => {
    fetchFarmDetails()
  }, [uniswapV3Staker, provider, pool])

  const apr = useMemo(() => {
    const time = endTime - startTime
    if (time === 0 || totalReward == null) {
      return '0'
    }
    if (poolTvlInXdc == '0') {
      return '> 10000'
    }

    const secondsInTheYear = '31536000'
    const inOneYear = totalReward.mul(rewardTokenPrice).div(1000).mul(secondsInTheYear).div(time)
    const res = formatUnits(inOneYear.mul(100).div(poolTvlInXdc.split('.')[0]), rewardDecimals).split('.')
    if (res[0].length > 1) {
      return res[0]
    }
    return `${res[0]}.${res[1].slice(0, 2)}`
  }, [startTime, endTime, totalReward, poolTvlInXdc, rewardTokenPrice, rewardDecimals])

  const durationData = useMemo<{ isEnded: boolean; color: string }>(() => {
    if (startTime * 1000 > Date.now()) {
      return { color: theme.blue600, isEnded: false }
    }
    if (endTime * 1000 > Date.now()) {
      return { color: theme.green500, isEnded: false }
    }
    return { color: theme.red400, isEnded: true }
  }, [startTime, endTime])

  const CurrencyLogos = useMemo(() => {
    if (tokens.token0.toLowerCase() === WXDC_XDC.address.toLowerCase()) {
      return (
        <DoubleCurrencyLogo currency0={token1 || undefined} currency1={token0 || undefined} size={24} margin={true} />
      )
    }

    return (
      <DoubleCurrencyLogo currency0={token0 || undefined} currency1={token1 || undefined} size={24} margin={true} />
    )
  }, [token0, token1, tokens])

  return (
    //todo data-testid
    <div data-testid={`token-table-row-${farmListIndex}`}>
      <StyledLink to={`/farm/${farmListIndex}`}>
        <TokenRow
          header={false}
          listNumber={<ClickableContent color="#797D9B">{farmListIndex + 1}</ClickableContent>}
          duration={
            <ClickableContent borderLeft={`2px solid ${durationData.color}`} padding="0 0 0 5px">
              <FlexCol width="160px" maxWidth="170px">
                <span style={{ fontSize: '0.9rem', lineHeight: '1.1rem' }}>
                  {format(startTime * 1000, 'dd MMM yyyy HH:mm')}
                </span>
                <span style={{ fontSize: '0.9rem', lineHeight: '1.1rem' }}>
                  {format(endTime * 1000, 'dd MMM yyyy HH:mm')}
                </span>
              </FlexCol>
            </ClickableContent>
          }
          pool={
            <ClickableContent gap="8px">
              {CurrencyLogos}
              {`${poolTitle}`}
            </ClickableContent>
          }
          poolTvl={
            <ClickableContent>
              {stakedTvlInXdc && xdcPrice
                ? formatNumber(
                    parseFloat(
                      formatEther(
                        stakedTvlInXdc
                          .mul((parseFloat(xdcPrice.current.toString()) * 1000000).toString().split('.')[0])
                          .div('1000000')
                      )
                    ),
                    NumberType.FiatTokenStats
                  )
                : '$0'}
            </ClickableContent>
          }
          rewardApr={
            <ClickableContent>
              {durationData.isEnded ? <EndedContainer>Ended</EndedContainer> : `${apr}%`}
            </ClickableContent>
          }
          vestingPeriod={<ClickableContent>{getFormattedTime(vestingPeriod * 1000)}</ClickableContent>}
          totalReward={
            <ClickableContent>
              <FlexCol>
                <span>{`${
                  totalReward
                    ? formatNumber(parseFloat(formatUnits(totalReward, rewardDecimals)), NumberType.NFTCollectionStats)
                    : '-'
                } ${rewardSymbol}`}</span>
                <span style={{ fontSize: '0.8rem', opacity: '0.5' }}>
                  {`~${
                    rewardTokenPrice && totalReward && xdcPrice
                      ? formatNumber(
                          parseFloat(
                            formatUnits(
                              totalReward
                                .mul(rewardTokenPrice)
                                .mul((parseFloat(xdcPrice.current.toString()) * 1000000).toString().split('.')[0])
                                .div('1000000000'),
                              rewardDecimals
                            )
                          ),
                          NumberType.FiatTokenStats
                        )
                      : '-'
                  }`}
                </span>
              </FlexCol>
            </ClickableContent>
          }
          first={true}
          last={farmListIndex === farmListLength - 1}
        />
      </StyledLink>
    </div>
  )
}
